import { Engagement } from "@oysterhr/schema"
import { SimpleMoney } from "@oysterhr/common"
import { Row, TitleRow } from "./components/Row"
import { PayrollRequestDetailsTable } from "./components/PayrollRequestDetailsTable"
import { PayrollRequestDetailsModal } from "./components/PayrollRequestDetailsModal"
import { useTranslation } from "react-i18next"
import { AdditionalInformationModal } from "./components/AdditionalInformationModal"
import EditableSection from "~/adminApp/pages/EmploymentPage/TabShared/EditableSection"
import { CompensationAndBenefitsModal } from "./components/CompensationAndBenefitsModal"
import { PayrollProcessingDetailsModal } from "./components/PayrollProcessingDetailsModal"
import { PayrollSpecialistCommentsRow } from "./components/PayrollSpecialistCommentsRow"
import { gql } from "@apollo/client"
import { useQuery } from "@oysterhr/core"
import { useMemo } from "react"
import { useFlags } from "@oysterhr/feature-flags"

type OffboardingSummaryProps = {
  engagement: Engagement
}

const PAYROLL_PROCESSING_NOTES_QUERY = gql`
  query PayrollProcessingNotes($postingContext: ID!) {
    staffNotes(postingContext: $postingContext) {
      id
      nodes {
        id
        createdAt
        createdBy {
          id
          name
        }
        content
      }
    }
  }
`

export const StaffOffboardingSummary = ({ engagement }: OffboardingSummaryProps) => {
  const { t } = useTranslation("colleague_interface/EmployeeProfilePage", {
    keyPrefix: "offboarding.offboardingSummary",
  })

  const { tGpOffboardingNewCountries } = useFlags()

  const termination = engagement.termination

  const additionalPayments = termination?.additionalTerminationPayments
  const additionalPaymentsRow =
    additionalPayments &&
    additionalPayments.map(payment =>
      payment.amountType === "duration" ? (
        <Row
          key={payment.id}
          label={t(`compensationDetails.${payment.offboardingPayType?.name}`)}
          content={`${payment.periodDuration} ${payment.period}`}
        />
      ) : (
        <Row
          key={payment.id}
          label={t(`compensationDetails.${payment.offboardingPayType?.name}`)}
          content={SimpleMoney.fromNullable(payment.amount, payment.currency, 1)?.format({
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        />
      ),
    )

  const ptoDaysRemaining = termination?.ptoInfo?.daysRemaining
  const ptoDaysRemainingAsInputed = termination?.ptoDaysRemaining

  const postingContext = termination?.payrollSpecialistNotes?.postingContext

  const { data } = useQuery(PAYROLL_PROCESSING_NOTES_QUERY, {
    pollInterval: 5000,
    variables: { postingContext },
  })

  const notes = useMemo(() => data?.staffNotes?.nodes || [], [data?.staffNotes?.nodes])

  return (
    <>
      <EditableSection
        title={t("payrollRequestDetails.title")}
        isEditable
        dataTestId="payroll-request-details-section"
      >
        {({ isEditing, setEditing }) => (
          <>
            <PayrollRequestDetailsTable engagement={engagement} />
            {isEditing && (
              <PayrollRequestDetailsModal
                engagement={engagement}
                setEditing={setEditing}
                modalTitle={t("payrollRequestDetails.edit")}
              />
            )}
          </>
        )}
      </EditableSection>

      {termination && (
        <EditableSection
          title={t("compensationDetails.title")}
          isEditable
          dataTestId="compensation-details"
        >
          {({ isEditing, setEditing }) => (
            <>
              <table className="w-full" key="compensationDetails">
                <tbody>
                  <Row
                    label={t("compensationDetails.ptoBalance")}
                    content={t("compensationDetails.daysRemaining", {
                      days: ptoDaysRemaining?.toString(),
                    })}
                  />
                  <Row
                    label={t("compensationDetails.ptoDetailsAccurate")}
                    content={termination?.ptoAccurate ? t("yes") : t("no")}
                  />
                  {!termination?.ptoAccurate && (
                    <Row
                      label={t("compensationDetails.ptoBalanceAsInputed")}
                      content={t("compensationDetails.daysRemaining", {
                        days: ptoDaysRemainingAsInputed,
                      })}
                    />
                  )}
                  {tGpOffboardingNewCountries &&
                    ptoDaysRemaining != null &&
                    ptoDaysRemaining < 0 && (
                      <Row
                        label={t("compensationDetails.deductNegativePto")}
                        content={termination?.deductNegativePto ? t("yes") : t("no")}
                      />
                    )}
                </tbody>
              </table>
              <table className="w-full" key="otherCompensation">
                <tbody>
                  <TitleRow title={t("otherCompensation.title")} />
                  <Row
                    label={t("otherCompensation.mutualTerminationAgreement")}
                    content={termination?.mutualTermination ? t("yes") : t("no")}
                  />
                  <Row
                    label={t("otherCompensation.additionalPayments")}
                    content={
                      additionalPayments && additionalPayments.length > 0 ? t("yes") : t("no")
                    }
                  />
                  {additionalPaymentsRow}
                </tbody>
              </table>
              {isEditing && (
                <CompensationAndBenefitsModal
                  setEditing={setEditing}
                  engagement={engagement}
                  termination={termination}
                />
              )}
            </>
          )}
        </EditableSection>
      )}

      {termination && (
        <EditableSection
          title={t("additionalDetails.title")}
          isEditable
          dataTestId="additional-details"
        >
          {({ isEditing, setEditing }) => (
            <>
              <table className="w-full" key="additionalDetails">
                <tbody>
                  {termination?.notes && (
                    <Row
                      label={t("additionalDetails.additionalNotes")}
                      content={termination?.notes}
                    />
                  )}
                </tbody>
                <tbody>
                  {termination?.notifyCustomerAdmin && (
                    <Row
                      label={t("additionalDetails.notifyCustomerAdmin")}
                      content={termination?.notifyCustomerAdmin ? t("yes") : t("no")}
                    />
                  )}
                </tbody>
                <tbody>
                  {termination?.roeCode && (
                    <Row label={t("additionalDetails.roeCode")} content={termination?.roeCode} />
                  )}
                </tbody>
              </table>
              {isEditing && (
                <AdditionalInformationModal
                  setEditing={setEditing}
                  engagement={engagement}
                  termination={termination}
                />
              )}
            </>
          )}
        </EditableSection>
      )}

      {termination && (
        <EditableSection
          title={t("payrollProcessingDetails.title")}
          isEditable
          dataTestId="payroll-processing-details-section"
        >
          {({ isEditing, setEditing }) => (
            <>
              <table className="w-full" key="payrollProcessingDetails">
                <tbody>
                  <PayrollSpecialistCommentsRow notes={notes} />
                </tbody>
              </table>
              {isEditing && (
                <PayrollProcessingDetailsModal
                  setEditing={setEditing}
                  engagement={engagement}
                  termination={termination}
                  notes={notes}
                />
              )}
            </>
          )}
        </EditableSection>
      )}
    </>
  )
}
